import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { endpoints, type Me } from "../lib/api";

/** Paste a link, send a bot. Calendar meetings are joined on their own, so this
 * is for the ones that aren't there: a call someone started in the moment, or
 * one on a calendar nobody here has connected.
 *
 * Several links at once work too, separated by spaces or commas. The backend
 * refuses anything that isn't Meet, Zoom or Teams, and refuses a meeting that
 * already has a bot — so nothing here has to decide what counts as valid. */
export function StartMeeting({ me }: { me: Me | null }) {
  const [links, setLinks] = useState("");
  const queryClient = useQueryClient();

  const start = useMutation({
    mutationFn: (urls: string[]) => endpoints.startMeetings(urls, me?.csrf_token ?? ""),
    onSuccess: () => {
      setLinks("");
      queryClient.invalidateQueries({ queryKey: ["meetings"] });
    },
  });

  const urls = links
    .split(/[\s,]+/)
    .map((line) => line.trim())
    .filter(Boolean);

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (urls.length > 0) start.mutate(urls);
  }

  let note: React.ReactNode = "The bot joins in a minute or two, records, and leaves when the meeting ends.";
  if (start.isSuccess && !start.error) {
    note = `${start.data.length === 1 ? "Bot sent." : `${start.data.length} bots sent.`} It shows under Happening now once it reaches the meeting.`;
  }

  return (
    <form className="send-bot" onSubmit={submit}>
      <div className="send-bot-bar">
        <svg className="send-bot-icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden>
          <path d="M15 10l4.5-2.5v9L15 14M4 7h11v10H4z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        </svg>
        <input
          value={links}
          onChange={(event) => setLinks(event.target.value)}
          placeholder="Paste a Google Meet, Teams or Zoom link"
          aria-label="Meeting link"
          spellCheck={false}
        />
        <button className="btn-primary" type="submit" disabled={urls.length === 0 || start.isPending}>
          {start.isPending ? "Sending…" : urls.length > 1 ? `Send ${urls.length} bots` : "Send the bot"}
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
            <path d="M7 17L17 7M9 7h8v8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      {start.error ? <p className="send-bot-note error-text">{(start.error as Error).message}</p> : <p className="send-bot-note">{note}</p>}
    </form>
  );
}
